import {
  DisplayIf,
  DisplayIf_Condition_Common,
  FormPath,
  FormPath_Object,
  DisplayIf_Condition,
  InputData,
  InputData_Flag,
  InputData_Protobuf,
  InputData_Store,
  InputData_Common,
  PrimitiveValue,
  MatchConditionString,
  DisplayIf_Condition_Comparator,
  SchemaAttachments_AttachmentItem,
  DisplayIf_Condition_Comparator_Simple_Value,
  FormComponent,
  FormComponentAddress,
  FormComponentDate,
  FormComponentAlert,
  FormComponentApi,
  FormComponentArray,
  FormComponentArrayGroup,
  FormComponentAttachmentDownload,
  FormComponentAutocomplete,
  FormComponentAttachmentSelect,
  FormComponentAttachmentUpload,
  FormComponentButton,
  FormComponentCheckbox,
  FormComponentCheckboxGroup,
  FormComponentChip,
  FormComponentCollection,
  FormComponentCollectionMulti,
  FormComponentCombobox,
  FormComponentComputed,
  FormComponentCurrency,
  FormComponentDeductibleMatrix,
  FormComponentDropdown,
  FormComponentImageSelect,
  FormComponentLabel,
  FormComponentNumber,
  FormComponentOption,
  FormComponentPercentage,
  FormComponentPhone,
  FormComponentRadio,
  FormComponentRateModifiers,
  FormComponentRollingYears,
  FormComponentSchedule,
  FormComponentScheduleMulti,
  FormComponentScheduleTable,
  FormComponentSelection,
  FormComponentSelection_Appearance,
  FormComponentSelection_Appearance_Card,
  FormComponentSelection_OptionObject,
  FormComponentSelection_OptionObject_Array,
  FormComponentSelection_OptionObject_Quote,
  FormComponentSeparator,
  FormComponentSpreadsheet,
  FormComponentStatic,
  FormComponentSwitch,
  FormComponentTable,
  FormComponentTemplateUploader,
  FormComponentText,
  FormComponentTextarea,
  FormComponentRadio_RadioButtons,
  FormComponentRadio_Button,
  FormComponentRadio_RadioButtons_Quotes,
  FormComponentRadio_RadioButtons_Boolean,
  FormComponentFieldLabelProps_Label,
  FormComponentFieldLabelProps_Label_DynamicLabelObject,
  FormDefault,
  FormDefault_DefaultValue,
  FormDefault_DefaultValue_Value,
  FormDefault_DefaultCalculated,
  FormDefault_DefaultData,
  FormDefault_DefaultDisplayIf,
  FormDefault_DefaultValue_Value_PrimitiveObjectArray,
  FormDefault_DefaultValue_Value_PrimitiveObject,
  FormDefault_DefaultCalculated_Calculation_TodayPlus,
  FormDefault_DefaultCalculated_Calculation_PriorityFields,
  FormDefault_DefaultCalculated_Calculation_EffectiveExpirationDate,
  FormDefault_DefaultCalculated_Calculation_AopPercentage,
  FormDefault_DefaultCalculated_Calculation_AopAmount,
  FormDefault_DefaultCalculated_Calculation_HighestField,
  FormDefault_DefaultCalculated_Calculation_SumFields,
  FormDefault_DefaultCalculated_Calculation_SumArrayFields,
  FormComponentDisabledProps,
  ValidationRule,
  Formatter,
  DropdownItems,
} from '@policyfly/protobuf/pfschema'
import { isEmptyArray, isObject } from '@policyfly/utils/type'

import { writeOrMap, writeMessage, writeOrMapRepeated } from './converters'
import { revertMessage, revertOrMap } from './reverters'
import { createMessageFieldInfo, writeOneofKind } from './utils'

import type { AttachmentItem } from '../types/schemas/attachments'
import type { DisabledProps } from '../types/shared/composables/disabled'
import type { Default, DefaultCalculated, DefaultValueType } from '../types/shared/composables/fieldDefault'
import type { FieldLabelProps } from '../types/shared/composables/fieldLabel'
import type { FormComponent as FormComponentSchema } from '../types/shared/formComponent'
import type { Button, RadioButtons } from '../types/shared/formComponent/radio'
import type { SelectionAppearance, SelectionOptions } from '../types/shared/formComponent/selection'
import type { FormPath as FormPathSchema } from '../types/shared/formPath'
import type { FormSource } from '../types/shared/formSource'
import type { InputDataResolved } from '../types/shared/inputData'
import type { AnyObject, PrimitiveValue as PrimitiveValueSchema, TSFixMe } from '@policyfly/types/common'
import type { IMessageType } from '@protobuf-ts/runtime'

const FORM_COMPONENT_FLATTEN_KEYS = [
  'external',
  // composables
  'base',
  'coverageIndicator',
  'dataTest',
  'disabled',
  'fieldDefault',
  'fieldLabel',
  'help',
  'hint',
  'input',
]

interface Flattener {
  key: string
  message: IMessageType<object>
}
function getFlatteners (message: IMessageType<object>, flattenKeys: string[]): Flattener[] {
  return flattenKeys.reduce<Flattener[]>((acc, key) => {
    const field = message.fields.find((f) => f.jsonName === key)
    if (field?.kind === 'message') {
      acc.push({ key, message: field.T() })
    }
    return acc
  }, [])
}

function createMapToValueFlatten<
  T extends object,
  U extends MessageMapperConfigValue
> (
  message: IMessageType<T>,
  flattenKeys: string[],
): NonNullable<MessageMapper<T, U>['mapValue']> {
  const flatteners = getFlatteners(message, flattenKeys)
  return (configValue) => {
    return {
      ...writeMessage(
        createMessageFieldInfo(message),
        configValue as object,
        message.create(),
      ),
      ...flatteners.reduce((acc, flattener) => {
        const v = writeOrMap(flattener.message, configValue as object)
        // don't assign empty messages or ones with only default values
        if (v && !flattener.message.equals(v, flattener.message.create())) {
          Object.assign(acc, { [flattener.key]: v })
        }
        return acc
      }, {}),
    }
  }
}

function createRevertMessageFlatten (message: IMessageType<object>, flattenKeys: string[]): NonNullable<MessageMapper['revertMessage']> {
  const flatteners = getFlatteners(message, flattenKeys)
  return (protobuf: AnyObject) => {
    const revertedMessage = revertMessage(
      createMessageFieldInfo(message),
      protobuf,
      {},
    )
    for (const flattener of flatteners) {
      const v = revertedMessage[flattener.key]
      if (v) {
        delete revertedMessage[flattener.key]
        Object.assign(revertedMessage, v)
      }
    }
    return revertedMessage
  }
}

function createMapToRepeatedMatchCondition<
  T extends object,
  U extends MessageMapperConfigValue
> (message: IMessageType<T>): MessageMapper<T, U>['mapToRepeated'] {
  return (configValue) => {
    if (Array.isArray(configValue)) {
      return writeOrMapRepeated(message, configValue)
    } else if (configValue !== null && configValue !== undefined) {
      return [{ value: configValue }] as T[]
    } else {
      return [] as T[]
    }
  }
}

function createRevertRepeatedMatchCondition<
  T extends { condition?: TSFixMe, value?: TSFixMe },
  U extends MessageMapperConfigValue
> (message: IMessageType<T>): MessageMapper<T, U>['revertRepeated'] {
  return (protobuf) => {
    if (!Array.isArray(protobuf) || isEmptyArray(protobuf)) return undefined
    if (protobuf.length === 1 && !protobuf[0].condition) {
      return protobuf[0].value
    }
    return protobuf.map((value) => {
      return revertOrMap(message, value)
    })
  }
}

function createNoopMapper<
  T extends object,
  U extends MessageMapperConfigValue
> (): MessageMapper<T, U> {
  return {
    mapValue (configValue) {
      return configValue as unknown as T
    },
    revertMessage (protobuf) {
      return protobuf as unknown as U
    },
  }
}

function createReverterOnlyMapper<
  T extends object,
  U extends MessageMapperConfigValue
> (mapper: Pick<MessageMapper<T, U>, 'revertMessage'>): MessageMapper<T, U> {
  return {
    mapValue (configValue) {
      return configValue as unknown as T
    },
    ...mapper,
  }
}

export type MessageMapperConfigValue = PrimitiveValueSchema | object

interface MessageMapper<
  Message extends object = object,
  ConfigValue extends MessageMapperConfigValue = MessageMapperConfigValue
> {
  /**
   * Function that returns default values that will be set if missing on the provided `configValue`.
   * This is useful as protobuf messages do not have default values we can control.
   */
  defaults?: () => Partial<ConfigValue>
  /**
   * Maps a configValue to a Protobuf message.
   */
  mapValue?(configValue: ConfigValue): Message | undefined
  /**
   * Maps a configValue to a repeated Protobuf message.
   * Useful if the configValue is not always guaranteed to be an array.
   *
   * If not specified then each item in the configValue will be mapped.
   */
  mapToRepeated?(configValue: ConfigValue): Message[] | undefined
  /**
   * Reverts a protobuf message back to a program configuration value.
   */
  revertMessage?(protobuf: Message): ConfigValue | undefined
  /**
   * Reverts a repeated Protobuf message to a configValue.
   * Useful if the configValue is not always guaranteed to be an array.
   *
   * If not specified then each item in the message will be mapped.
   */
  revertRepeated?(protobuf: Message[]): ConfigValue | undefined
}
class MessageMappers extends Map<string, MessageMapper> {
  addMapper<Message extends object, ConfigValue extends MessageMapperConfigValue> (message: IMessageType<Message>, mapper: MessageMapper<Message, ConfigValue>): this {
    return this.set(message.typeName, mapper)
  }

  getMapper<Message extends object, ConfigValue extends MessageMapperConfigValue>(message: IMessageType<Message>): MessageMapper<Message, ConfigValue> | undefined {
    return this.get(message.typeName) as MessageMapper<Message, ConfigValue> | undefined
  }
}
/**
 * Mappers transform a program configuration value into a protobuf message.
 */
export const messageMappers = new MessageMappers()
// shared
messageMappers.addMapper<DisplayIf, DisplayIf>(DisplayIf, createNoopMapper())
messageMappers.addMapper<DisplayIf_Condition, DisplayIf_Condition>(DisplayIf_Condition, createNoopMapper())
messageMappers.addMapper<DisplayIf_Condition_Common, DisplayIf_Condition_Common>(DisplayIf_Condition_Common, createNoopMapper())
messageMappers.addMapper<DisplayIf_Condition_Comparator, DisplayIf_Condition_Comparator>(DisplayIf_Condition_Comparator, createNoopMapper())
messageMappers.addMapper<DisplayIf_Condition_Comparator_Simple_Value, DisplayIf_Condition_Comparator_Simple_Value>(DisplayIf_Condition_Comparator_Simple_Value, createNoopMapper())

messageMappers.addMapper<FormPath, FormPathSchema>(FormPath, createReverterOnlyMapper({
  revertMessage (protobuf) {
    switch (protobuf.value.oneofKind) {
      case 'object': return revertOrMap(FormPath_Object, protobuf.value.object) as FormPathSchema
      case 'number': return protobuf.value.number
      case 'string': return protobuf.value.string
      default: return ''
    }
  },
}))
messageMappers.addMapper<InputData, InputDataResolved<FormSource>>(InputData, createReverterOnlyMapper({
  revertMessage (protobuf) {
    if (!protobuf.source || !protobuf.source.oneofKind) return undefined
    const mapMessage = (() => {
      switch (protobuf.source.oneofKind) {
        case 'pb': return InputData_Protobuf
        case 'store': return InputData_Store
        case 'flag': return InputData_Flag
        default: return InputData_Common
      }
    })()
    return {
      source: protobuf.source.oneofKind,
      // @ts-expect-error(fixable): Cannot infer the oneofKind here
      ...revertOrMap(mapMessage, protobuf.source[protobuf.source.oneofKind]),
    } as InputDataResolved<FormSource>
  },
}))
messageMappers.addMapper<PrimitiveValue, PrimitiveValueSchema>(PrimitiveValue, {
  mapValue (configValue) {
    switch (typeof configValue) {
      case 'string': return { value: { oneofKind: 'string', string: configValue } }
      case 'number': return { value: { oneofKind: 'number', number: configValue } }
      case 'boolean': return { value: { oneofKind: 'bool', bool: configValue } }
    }
    if (configValue === null) {
      return { value: { oneofKind: 'null', null: true } }
    }
    return { value: { oneofKind: 'undefined', undefined: true } }
  },
  revertMessage (protobuf) {
    switch (protobuf.value.oneofKind) {
      case 'bool': return protobuf.value.bool
      case 'number': return protobuf.value.number
      case 'string': return protobuf.value.string
      case 'null': return null
      case 'undefined': return undefined
    }
  },
})
messageMappers.addMapper<MatchConditionString, MessageMapperConfigValue>(MatchConditionString, {
  mapToRepeated: createMapToRepeatedMatchCondition(MatchConditionString),
  revertRepeated: createRevertRepeatedMatchCondition(MatchConditionString),
})
// form components
/**
 * Maps each `oneofKind` of the {@link FormComponent} `type` oneof to its variant message type.
 * `oneofKind` values are the `localName` of the oneof member fields.
 */
const formComponentTypeMessages = new Map<string, IMessageType<object>>(
  FormComponent.fields.flatMap((field) => {
    if (field.oneof !== 'type' || field.kind !== 'message') return []
    return [[field.localName, field.T()] as const]
  }),
)

messageMappers.addMapper<FormComponent, FormComponentSchema<FormSource>>(FormComponent, {
  mapValue (configValue) {
    const kind = configValue?.type?.oneofKind
    if (!kind) return undefined
    const message = formComponentTypeMessages.get(kind)
    if (!message) {
      console.warn(`Unknown FormComponent type: ${kind}`)
      return undefined
    }
    return {
      type: writeOneofKind(kind, writeOrMap(message, (configValue.type as AnyObject)[kind] as object)!),
    } as FormComponent
  },
  revertMessage (protobuf) {
    const kind = protobuf.type.oneofKind
    if (!kind) return undefined
    const message = formComponentTypeMessages.get(kind)
    if (!message) {
      console.warn(`Unknown FormComponent type: ${kind}`)
      return undefined
    }
    return {
      type: writeOneofKind(kind, revertOrMap(message, (protobuf.type as AnyObject)[kind] as object)),
    } as FormComponentSchema<FormSource>
  },
})
messageMappers.addMapper<FormComponentAddress, MessageMapperConfigValue>(FormComponentAddress, {
  mapValue: createMapToValueFlatten(FormComponentAddress, FORM_COMPONENT_FLATTEN_KEYS),
  revertMessage: createRevertMessageFlatten(FormComponentAddress, FORM_COMPONENT_FLATTEN_KEYS),
})
messageMappers.addMapper<FormComponentAlert, MessageMapperConfigValue>(FormComponentAlert, {
  mapValue: createMapToValueFlatten(FormComponentAlert, FORM_COMPONENT_FLATTEN_KEYS),
  revertMessage: createRevertMessageFlatten(FormComponentAlert, FORM_COMPONENT_FLATTEN_KEYS),
})
messageMappers.addMapper<FormComponentApi, MessageMapperConfigValue>(FormComponentApi, {
  mapValue: createMapToValueFlatten(FormComponentApi, FORM_COMPONENT_FLATTEN_KEYS),
  revertMessage: createRevertMessageFlatten(FormComponentApi, FORM_COMPONENT_FLATTEN_KEYS),
})
messageMappers.addMapper<FormComponentArray, MessageMapperConfigValue>(FormComponentArray, {
  mapValue: createMapToValueFlatten(FormComponentArray, FORM_COMPONENT_FLATTEN_KEYS),
  revertMessage: createRevertMessageFlatten(FormComponentArray, FORM_COMPONENT_FLATTEN_KEYS),
})
messageMappers.addMapper<FormComponentArrayGroup, MessageMapperConfigValue>(FormComponentArrayGroup, {
  mapValue: createMapToValueFlatten(FormComponentArrayGroup, FORM_COMPONENT_FLATTEN_KEYS),
  revertMessage: createRevertMessageFlatten(FormComponentArrayGroup, FORM_COMPONENT_FLATTEN_KEYS),
})
messageMappers.addMapper<FormComponentAttachmentDownload, MessageMapperConfigValue>(FormComponentAttachmentDownload, {
  mapValue: createMapToValueFlatten(FormComponentAttachmentDownload, FORM_COMPONENT_FLATTEN_KEYS),
  revertMessage: createRevertMessageFlatten(FormComponentAttachmentDownload, FORM_COMPONENT_FLATTEN_KEYS),
})
messageMappers.addMapper<FormComponentAutocomplete, MessageMapperConfigValue>(FormComponentAutocomplete, {
  mapValue: createMapToValueFlatten(FormComponentAutocomplete, FORM_COMPONENT_FLATTEN_KEYS),
  revertMessage: createRevertMessageFlatten(FormComponentAutocomplete, FORM_COMPONENT_FLATTEN_KEYS),
})
messageMappers.addMapper<FormComponentAttachmentSelect, MessageMapperConfigValue>(FormComponentAttachmentSelect, {
  mapValue: createMapToValueFlatten(FormComponentAttachmentSelect, FORM_COMPONENT_FLATTEN_KEYS),
  revertMessage: createRevertMessageFlatten(FormComponentAttachmentSelect, FORM_COMPONENT_FLATTEN_KEYS),
})
messageMappers.addMapper<FormComponentAttachmentUpload, MessageMapperConfigValue>(FormComponentAttachmentUpload, {
  mapValue: createMapToValueFlatten(FormComponentAttachmentUpload, FORM_COMPONENT_FLATTEN_KEYS),
  revertMessage: createRevertMessageFlatten(FormComponentAttachmentUpload, FORM_COMPONENT_FLATTEN_KEYS),
})
messageMappers.addMapper<FormComponentButton, MessageMapperConfigValue>(FormComponentButton, {
  mapValue: createMapToValueFlatten(FormComponentButton, FORM_COMPONENT_FLATTEN_KEYS),
  revertMessage: createRevertMessageFlatten(FormComponentButton, FORM_COMPONENT_FLATTEN_KEYS),
})
messageMappers.addMapper<FormComponentCheckbox, MessageMapperConfigValue>(FormComponentCheckbox, {
  mapValue: createMapToValueFlatten(FormComponentCheckbox, FORM_COMPONENT_FLATTEN_KEYS),
  revertMessage: createRevertMessageFlatten(FormComponentCheckbox, FORM_COMPONENT_FLATTEN_KEYS),
})
messageMappers.addMapper<FormComponentCheckboxGroup, MessageMapperConfigValue>(FormComponentCheckboxGroup, {
  mapValue: createMapToValueFlatten(FormComponentCheckboxGroup, FORM_COMPONENT_FLATTEN_KEYS),
  revertMessage: createRevertMessageFlatten(FormComponentCheckboxGroup, FORM_COMPONENT_FLATTEN_KEYS),
})
messageMappers.addMapper<FormComponentChip, MessageMapperConfigValue>(FormComponentChip, {
  mapValue: createMapToValueFlatten(FormComponentChip, FORM_COMPONENT_FLATTEN_KEYS),
  revertMessage: createRevertMessageFlatten(FormComponentChip, FORM_COMPONENT_FLATTEN_KEYS),
})
messageMappers.addMapper<FormComponentCollection, MessageMapperConfigValue>(FormComponentCollection, {
  mapValue: createMapToValueFlatten(FormComponentCollection, FORM_COMPONENT_FLATTEN_KEYS),
  revertMessage: createRevertMessageFlatten(FormComponentCollection, FORM_COMPONENT_FLATTEN_KEYS),
})
messageMappers.addMapper<FormComponentCollectionMulti, MessageMapperConfigValue>(FormComponentCollectionMulti, {
  mapValue: createMapToValueFlatten(FormComponentCollectionMulti, FORM_COMPONENT_FLATTEN_KEYS),
  revertMessage: createRevertMessageFlatten(FormComponentCollectionMulti, FORM_COMPONENT_FLATTEN_KEYS),
})
messageMappers.addMapper<FormComponentCombobox, MessageMapperConfigValue>(FormComponentCombobox, {
  mapValue: createMapToValueFlatten(FormComponentCombobox, FORM_COMPONENT_FLATTEN_KEYS),
  revertMessage: createRevertMessageFlatten(FormComponentCombobox, FORM_COMPONENT_FLATTEN_KEYS),
})
messageMappers.addMapper<FormComponentComputed, MessageMapperConfigValue>(FormComponentComputed, {
  mapValue: createMapToValueFlatten(FormComponentComputed, FORM_COMPONENT_FLATTEN_KEYS),
  revertMessage: createRevertMessageFlatten(FormComponentComputed, FORM_COMPONENT_FLATTEN_KEYS),
})
messageMappers.addMapper<FormComponentCurrency, MessageMapperConfigValue>(FormComponentCurrency, {
  mapValue: createMapToValueFlatten(FormComponentCurrency, FORM_COMPONENT_FLATTEN_KEYS),
  revertMessage: createRevertMessageFlatten(FormComponentCurrency, FORM_COMPONENT_FLATTEN_KEYS),
})
messageMappers.addMapper<FormComponentDate, MessageMapperConfigValue>(FormComponentDate, {
  mapValue: createMapToValueFlatten(FormComponentDate, FORM_COMPONENT_FLATTEN_KEYS),
  revertMessage: createRevertMessageFlatten(FormComponentDate, FORM_COMPONENT_FLATTEN_KEYS),
})
messageMappers.addMapper<FormComponentDeductibleMatrix, MessageMapperConfigValue>(FormComponentDeductibleMatrix, {
  mapValue: createMapToValueFlatten(FormComponentDeductibleMatrix, FORM_COMPONENT_FLATTEN_KEYS),
  revertMessage: createRevertMessageFlatten(FormComponentDeductibleMatrix, FORM_COMPONENT_FLATTEN_KEYS),
})
messageMappers.addMapper<FormComponentDropdown, MessageMapperConfigValue>(FormComponentDropdown, {
  mapValue: createMapToValueFlatten(FormComponentDropdown, FORM_COMPONENT_FLATTEN_KEYS),
  revertMessage: createRevertMessageFlatten(FormComponentDropdown, FORM_COMPONENT_FLATTEN_KEYS),
})
messageMappers.addMapper<FormComponentImageSelect, MessageMapperConfigValue>(FormComponentImageSelect, {
  mapValue: createMapToValueFlatten(FormComponentImageSelect, FORM_COMPONENT_FLATTEN_KEYS),
  revertMessage: createRevertMessageFlatten(FormComponentImageSelect, FORM_COMPONENT_FLATTEN_KEYS),
})
messageMappers.addMapper<FormComponentLabel, MessageMapperConfigValue>(FormComponentLabel, {
  mapValue: createMapToValueFlatten(FormComponentLabel, FORM_COMPONENT_FLATTEN_KEYS),
  revertMessage: createRevertMessageFlatten(FormComponentLabel, FORM_COMPONENT_FLATTEN_KEYS),
})
messageMappers.addMapper<FormComponentNumber, MessageMapperConfigValue>(FormComponentNumber, {
  mapValue: createMapToValueFlatten(FormComponentNumber, FORM_COMPONENT_FLATTEN_KEYS),
  revertMessage: createRevertMessageFlatten(FormComponentNumber, FORM_COMPONENT_FLATTEN_KEYS),
})
messageMappers.addMapper<FormComponentOption, MessageMapperConfigValue>(FormComponentOption, {
  mapValue: createMapToValueFlatten(FormComponentOption, FORM_COMPONENT_FLATTEN_KEYS),
  revertMessage: createRevertMessageFlatten(FormComponentOption, FORM_COMPONENT_FLATTEN_KEYS),
})
messageMappers.addMapper<FormComponentPercentage, MessageMapperConfigValue>(FormComponentPercentage, {
  mapValue: createMapToValueFlatten(FormComponentPercentage, FORM_COMPONENT_FLATTEN_KEYS),
  revertMessage: createRevertMessageFlatten(FormComponentPercentage, FORM_COMPONENT_FLATTEN_KEYS),
})
messageMappers.addMapper<FormComponentPhone, MessageMapperConfigValue>(FormComponentPhone, {
  mapValue: createMapToValueFlatten(FormComponentPhone, FORM_COMPONENT_FLATTEN_KEYS),
  revertMessage: createRevertMessageFlatten(FormComponentPhone, FORM_COMPONENT_FLATTEN_KEYS),
})
messageMappers.addMapper<FormComponentRadio, MessageMapperConfigValue>(FormComponentRadio, {
  mapValue: createMapToValueFlatten(FormComponentRadio, FORM_COMPONENT_FLATTEN_KEYS),
  revertMessage: createRevertMessageFlatten(FormComponentRadio, FORM_COMPONENT_FLATTEN_KEYS),
})
messageMappers.addMapper<FormComponentRateModifiers, MessageMapperConfigValue>(FormComponentRateModifiers, {
  mapValue: createMapToValueFlatten(FormComponentRateModifiers, FORM_COMPONENT_FLATTEN_KEYS),
  revertMessage: createRevertMessageFlatten(FormComponentRateModifiers, FORM_COMPONENT_FLATTEN_KEYS),
})
messageMappers.addMapper<FormComponentRollingYears, MessageMapperConfigValue>(FormComponentRollingYears, {
  mapValue: createMapToValueFlatten(FormComponentRollingYears, FORM_COMPONENT_FLATTEN_KEYS),
  revertMessage: createRevertMessageFlatten(FormComponentRollingYears, FORM_COMPONENT_FLATTEN_KEYS),
})
messageMappers.addMapper<FormComponentSchedule, MessageMapperConfigValue>(FormComponentSchedule, {
  mapValue: createMapToValueFlatten(FormComponentSchedule, FORM_COMPONENT_FLATTEN_KEYS),
  revertMessage: createRevertMessageFlatten(FormComponentSchedule, FORM_COMPONENT_FLATTEN_KEYS),
})
messageMappers.addMapper<FormComponentScheduleMulti, MessageMapperConfigValue>(FormComponentScheduleMulti, {
  mapValue: createMapToValueFlatten(FormComponentScheduleMulti, FORM_COMPONENT_FLATTEN_KEYS),
  revertMessage: createRevertMessageFlatten(FormComponentScheduleMulti, FORM_COMPONENT_FLATTEN_KEYS),
})
messageMappers.addMapper<FormComponentScheduleTable, MessageMapperConfigValue>(FormComponentScheduleTable, {
  mapValue: createMapToValueFlatten(FormComponentScheduleTable, FORM_COMPONENT_FLATTEN_KEYS),
  revertMessage: createRevertMessageFlatten(FormComponentScheduleTable, FORM_COMPONENT_FLATTEN_KEYS),
})
messageMappers.addMapper<FormComponentSelection, MessageMapperConfigValue>(FormComponentSelection, {
  mapValue: createMapToValueFlatten(FormComponentSelection, FORM_COMPONENT_FLATTEN_KEYS),
  revertMessage: createRevertMessageFlatten(FormComponentSelection, FORM_COMPONENT_FLATTEN_KEYS),
})
messageMappers.addMapper<FormComponentSeparator, MessageMapperConfigValue>(FormComponentSeparator, {
  mapValue: createMapToValueFlatten(FormComponentSeparator, FORM_COMPONENT_FLATTEN_KEYS),
  revertMessage: createRevertMessageFlatten(FormComponentSeparator, FORM_COMPONENT_FLATTEN_KEYS),
})
messageMappers.addMapper<FormComponentSpreadsheet, MessageMapperConfigValue>(FormComponentSpreadsheet, {
  mapValue: createMapToValueFlatten(FormComponentSpreadsheet, FORM_COMPONENT_FLATTEN_KEYS),
  revertMessage: createRevertMessageFlatten(FormComponentSpreadsheet, FORM_COMPONENT_FLATTEN_KEYS),
})
messageMappers.addMapper<FormComponentStatic, MessageMapperConfigValue>(FormComponentStatic, {
  mapValue: createMapToValueFlatten(FormComponentStatic, FORM_COMPONENT_FLATTEN_KEYS),
  revertMessage: createRevertMessageFlatten(FormComponentStatic, FORM_COMPONENT_FLATTEN_KEYS),
})
messageMappers.addMapper<FormComponentSwitch, MessageMapperConfigValue>(FormComponentSwitch, {
  mapValue: createMapToValueFlatten(FormComponentSwitch, FORM_COMPONENT_FLATTEN_KEYS),
  revertMessage: createRevertMessageFlatten(FormComponentSwitch, FORM_COMPONENT_FLATTEN_KEYS),
})
messageMappers.addMapper<FormComponentTable, MessageMapperConfigValue>(FormComponentTable, {
  mapValue: createMapToValueFlatten(FormComponentTable, FORM_COMPONENT_FLATTEN_KEYS),
  revertMessage: createRevertMessageFlatten(FormComponentTable, FORM_COMPONENT_FLATTEN_KEYS),
})
messageMappers.addMapper<FormComponentTemplateUploader, MessageMapperConfigValue>(FormComponentTemplateUploader, {
  mapValue: createMapToValueFlatten(FormComponentTemplateUploader, FORM_COMPONENT_FLATTEN_KEYS),
  revertMessage: createRevertMessageFlatten(FormComponentTemplateUploader, FORM_COMPONENT_FLATTEN_KEYS),
})
messageMappers.addMapper<FormComponentText, MessageMapperConfigValue>(FormComponentText, {
  mapValue: createMapToValueFlatten(FormComponentText, FORM_COMPONENT_FLATTEN_KEYS),
  revertMessage: createRevertMessageFlatten(FormComponentText, FORM_COMPONENT_FLATTEN_KEYS),
})
messageMappers.addMapper<FormComponentTextarea, MessageMapperConfigValue>(FormComponentTextarea, {
  mapValue: createMapToValueFlatten(FormComponentTextarea, FORM_COMPONENT_FLATTEN_KEYS),
  revertMessage: createRevertMessageFlatten(FormComponentTextarea, FORM_COMPONENT_FLATTEN_KEYS),
})
messageMappers.addMapper<FormComponentRadio_RadioButtons, RadioButtons<FormSource>>(FormComponentRadio_RadioButtons, {
  mapValue (configValue) {
    if (!configValue) return undefined
    if (Array.isArray(configValue)) {
      return {
        preset: {
          oneofKind: 'array',
          array: {
            buttons: writeOrMapRepeated(FormComponentRadio_Button, configValue),
          },
        },
      }
    }
    switch (configValue.preset) {
      case 'quotes':
        return {
          preset: {
            oneofKind: 'quotes',
            quotes: writeOrMap(FormComponentRadio_RadioButtons_Quotes, {})!,
          },
        }
      case 'boolean':
        return {
          preset: {
            oneofKind: 'boolean',
            boolean: configValue.params
              ? writeOrMap(FormComponentRadio_RadioButtons_Boolean, configValue.params)!
              : {},
          },
        }
    }
  },
  revertMessage (protobuf) {
    switch (protobuf.preset.oneofKind) {
      case 'array':
        return protobuf.preset.array.buttons.map((button) => {
          return revertOrMap(FormComponentRadio_Button, button)
        }) as Button<FormSource>[]
      case 'quotes':
        return {
          preset: 'quotes',
        }
      case 'boolean':
        return {
          preset: 'boolean',
          params: revertOrMap(FormComponentRadio_RadioButtons_Boolean, protobuf.preset.boolean),
        }
    }
    return undefined
  },
})
messageMappers.addMapper<FormComponentSelection_OptionObject, SelectionOptions<FormSource>>(FormComponentSelection_OptionObject, {
  mapValue (configValue) {
    if (!configValue) return undefined
    switch (configValue.type) {
      case 'array':
        return {
          type: {
            oneofKind: 'array',
            array: writeOrMap(FormComponentSelection_OptionObject_Array, configValue.params)!,
          },
        }
      case 'quote':
        return {
          type: {
            oneofKind: 'quote',
            quote: writeOrMap(FormComponentSelection_OptionObject_Quote, configValue.params ?? {})!,
          },
        }
    }
  },
  revertMessage (protobuf) {
    switch (protobuf.type.oneofKind) {
      case 'array':
        return {
          type: 'array',
          params: revertOrMap(FormComponentSelection_OptionObject_Array, protobuf.type.array),
        } as SelectionOptions<FormSource>
      case 'quote':
        return {
          type: 'quote',
          params: revertOrMap(FormComponentSelection_OptionObject_Quote, protobuf.type.quote),
        } as SelectionOptions<FormSource>
    }
    return undefined
  },
})
messageMappers.addMapper<FormComponentSelection_Appearance, SelectionAppearance<FormSource>>(FormComponentSelection_Appearance, {
  mapValue (configValue) {
    if (!configValue) return undefined
    switch (configValue.type) {
      case 'card':
        return {
          type: {
            oneofKind: 'card',
            card: writeOrMap(FormComponentSelection_Appearance_Card, configValue.params ?? {})!,
          },
        }
    }
  },
  revertMessage (protobuf) {
    switch (protobuf.type.oneofKind) {
      case 'card':
        return {
          type: 'card',
          params: revertOrMap(FormComponentSelection_Appearance_Card, protobuf.type.card),
        } as SelectionAppearance<FormSource>
    }
    return undefined
  },
})
// form component composables
messageMappers.addMapper<FormDefault, Default<DefaultValueType>>(FormDefault, {
  mapValue (configValue) {
    if (!configValue) return undefined
    switch (configValue.type) {
      case 'calculated':
        return {
          type: {
            oneofKind: 'calculated',
            calculated: writeOrMap(FormDefault_DefaultCalculated, configValue)!,
          },
        }
      case 'data':
        return {
          type: {
            oneofKind: 'data',
            data: writeOrMap(FormDefault_DefaultData, configValue)!,
          },
        }
      case 'displayIf':
        return {
          type: {
            oneofKind: 'displayIf',
            displayIf: writeOrMap(FormDefault_DefaultDisplayIf, configValue)!,
          },
        }
      case 'value':
        return {
          type: {
            oneofKind: 'value',
            value: writeOrMap(FormDefault_DefaultValue, configValue)!,
          },
        }
    }
  },
  revertMessage (protobuf) {
    switch (protobuf.type.oneofKind) {
      case 'calculated':
        return {
          type: 'calculated',
          ...revertOrMap(FormDefault_DefaultCalculated, protobuf.type.calculated),
        } as Default<DefaultValueType>
      case 'data':
        return {
          type: 'data',
          ...revertOrMap(FormDefault_DefaultData, protobuf.type.data),
        } as Default<DefaultValueType>
      case 'displayIf':
        return {
          type: 'displayIf',
          ...revertOrMap(FormDefault_DefaultDisplayIf, protobuf.type.displayIf),
        } as Default<DefaultValueType>
      case 'value':
        return {
          type: 'value',
          ...revertOrMap(FormDefault_DefaultValue, protobuf.type.value),
        } as Default<DefaultValueType>
    }
  },
})
messageMappers.addMapper<FormDefault_DefaultCalculated, DefaultCalculated>(FormDefault_DefaultCalculated, {
  mapValue (configValue) {
    const flattener = createMapToValueFlatten(FormDefault_DefaultCalculated, ['shared'])
    const flattenCopy = { ...configValue }
    // @ts-expect-error(fixable): We want to operate on a partial here
    delete flattenCopy.calculation
    if ('params' in flattenCopy) delete flattenCopy.params
    const flattened = flattener(flattenCopy) as FormDefault_DefaultCalculated
    flattened.calculation = (() => {
      switch (configValue.calculation) {
        case 'today':
        case 'sraeTermBasedExpiration':
        case 'firstDropdownOption':
        case 'bcapdTowingStorageLimit':
          return {
            params: writeOneofKind(
              configValue.calculation,
              {},
            ),
          }
        case 'todayPlus':
          return {
            params: writeOneofKind(
              configValue.calculation,
              writeOrMap(FormDefault_DefaultCalculated_Calculation_TodayPlus, configValue.params)!,
            ),
          }
        case 'priorityFields':
          return {
            params: writeOneofKind(
              configValue.calculation,
              writeOrMap(FormDefault_DefaultCalculated_Calculation_PriorityFields, configValue.params)!,
            ),
          }
        case 'effectiveDate':
        case 'expirationDate':
          return {
            params: writeOneofKind(
              configValue.calculation,
              writeOrMap(FormDefault_DefaultCalculated_Calculation_EffectiveExpirationDate, configValue.params!)!,
            ),
          }
        case 'aopPercentage':
          return {
            params: writeOneofKind(
              configValue.calculation,
              writeOrMap(FormDefault_DefaultCalculated_Calculation_AopPercentage, configValue.params)!,
            ),
          }
        case 'aopAmount':
          return {
            params: writeOneofKind(
              configValue.calculation,
              writeOrMap(FormDefault_DefaultCalculated_Calculation_AopAmount, configValue.params)!,
            ),
          }
        case 'highestField':
          return {
            params: writeOneofKind(
              configValue.calculation,
              writeOrMap(FormDefault_DefaultCalculated_Calculation_HighestField, configValue.params)!,
            ),
          }
        case 'sumFields':
          return {
            params: writeOneofKind(
              configValue.calculation,
              writeOrMap(FormDefault_DefaultCalculated_Calculation_SumFields, configValue.params)!,
            ),
          }
        case 'sumArrayFields':
          return {
            params: writeOneofKind(
              configValue.calculation,
              writeOrMap(FormDefault_DefaultCalculated_Calculation_SumArrayFields, configValue.params)!,
            ),
          }
      }
    })()
    return flattened
  },
  revertMessage (protobuf) {
    const unflattener = createRevertMessageFlatten(FormDefault_DefaultCalculated, ['shared'])
    const unflattened = unflattener(protobuf) as DefaultCalculated
    const calculationAndParams = (() => {
      const calculation = protobuf.calculation?.params.oneofKind
      if (!calculation) return { calculation: undefined, params: undefined }
      // @ts-expect-error(fixable): Cannot infer oneof kind
      const params = protobuf.calculation.params[calculation]
      switch (calculation) {
        case 'today':
        case 'sraeTermBasedExpiration':
        case 'firstDropdownOption':
        case 'bcapdTowingStorageLimit':
          return {
            calculation,
            params: undefined,
          }
        case 'todayPlus':
          return {
            calculation,
            params: revertOrMap(FormDefault_DefaultCalculated_Calculation_TodayPlus, params),
          }
        case 'priorityFields':
          return {
            calculation,
            params: revertOrMap(FormDefault_DefaultCalculated_Calculation_PriorityFields, params),
          }
        case 'effectiveDate':
        case 'expirationDate':
          return {
            calculation,
            params: revertOrMap(FormDefault_DefaultCalculated_Calculation_EffectiveExpirationDate, params),
          }
        case 'aopPercentage':
          return {
            calculation,
            params: revertOrMap(FormDefault_DefaultCalculated_Calculation_AopPercentage, params),
          }
        case 'aopAmount':
          return {
            calculation,
            params: revertOrMap(FormDefault_DefaultCalculated_Calculation_AopAmount, params),
          }
        case 'highestField':
          return {
            calculation,
            params: revertOrMap(FormDefault_DefaultCalculated_Calculation_HighestField, params),
          }
        case 'sumFields':
          return {
            calculation,
            params: revertOrMap(FormDefault_DefaultCalculated_Calculation_SumFields, params),
          }
        case 'sumArrayFields':
          return {
            calculation,
            params: revertOrMap(FormDefault_DefaultCalculated_Calculation_SumArrayFields, params),
          }
      }
    })()
    Object.assign(unflattened, calculationAndParams)
    return unflattened
  },
})
messageMappers.addMapper<FormDefault_DefaultData, MessageMapperConfigValue>(FormDefault_DefaultData, {
  mapValue: createMapToValueFlatten(FormDefault_DefaultData, ['shared']),
  revertMessage: createRevertMessageFlatten(FormDefault_DefaultData, ['shared']),
})
messageMappers.addMapper<FormDefault_DefaultDisplayIf, MessageMapperConfigValue>(FormDefault_DefaultDisplayIf, {
  mapValue: createMapToValueFlatten(FormDefault_DefaultDisplayIf, ['shared']),
  revertMessage: createRevertMessageFlatten(FormDefault_DefaultDisplayIf, ['shared']),
})
messageMappers.addMapper<FormDefault_DefaultValue, MessageMapperConfigValue>(FormDefault_DefaultValue, {
  mapValue: createMapToValueFlatten(FormDefault_DefaultValue, ['shared']),
  revertMessage: createRevertMessageFlatten(FormDefault_DefaultValue, ['shared']),
})
messageMappers.addMapper<FormDefault_DefaultValue_Value, DefaultValueType>(FormDefault_DefaultValue_Value, {
  mapValue (configValue) {
    if (Array.isArray(configValue)) {
      return {
        type: {
          oneofKind: 'array',
          array: writeOrMap(FormDefault_DefaultValue_Value_PrimitiveObjectArray, configValue)!,
        },
      }
    }
    if (isObject(configValue)) {
      return {
        type: {
          oneofKind: 'object',
          object: writeOrMap(FormDefault_DefaultValue_Value_PrimitiveObject, configValue)!,
        },
      }
    }
    return {
      type: {
        oneofKind: 'primitive',
        primitive: writeOrMap(PrimitiveValue, configValue as unknown as object)!,
      },
    }
  },
  revertMessage (protobuf) {
    switch (protobuf.type.oneofKind) {
      case 'array':
        return revertOrMap(FormDefault_DefaultValue_Value_PrimitiveObjectArray, protobuf.type.array) as DefaultValueType
      case 'object':
        return revertOrMap(FormDefault_DefaultValue_Value_PrimitiveObject, protobuf.type.object) as DefaultValueType
      case 'primitive':
        return revertOrMap(PrimitiveValue, protobuf.type.primitive) as DefaultValueType
      case undefined:
        return undefined
    }
  },
})
messageMappers.addMapper<FormComponentDisabledProps, DisabledProps<FormSource>>(FormComponentDisabledProps, {
  revertMessage (protobuf) {
    const disabledProps = revertMessage(
      createMessageFieldInfo(FormComponentDisabledProps),
      protobuf,
      {},
    ) as DisabledProps<FormSource>
    if (isEmptyArray(disabledProps.disabledFormTypes)) delete disabledProps.disabledFormTypes
    return disabledProps
  },
})
messageMappers.addMapper<FormComponentFieldLabelProps_Label, NonNullable<FieldLabelProps<FormSource>['label']>>(FormComponentFieldLabelProps_Label, {
  mapValue (configValue) {
    if (!configValue) return undefined
    if (typeof configValue === 'string') {
      return {
        type: {
          oneofKind: 'string',
          string: configValue,
        },
      }
    } else {
      return {
        type: {
          oneofKind: 'object',
          object: writeOrMap(FormComponentFieldLabelProps_Label_DynamicLabelObject, configValue)!,
        },
      }
    }
  },
  revertMessage (protobuf) {
    switch (protobuf.type.oneofKind) {
      case 'string':
        return protobuf.type.string
      case 'object':
        return revertOrMap(
          FormComponentFieldLabelProps_Label_DynamicLabelObject,
          protobuf.type.object,
        ) as FieldLabelProps<FormSource>['label']
    }
  },
})
messageMappers.addMapper<ValidationRule, ValidationRule>(ValidationRule, createNoopMapper())
messageMappers.addMapper<Formatter, Formatter>(Formatter, createNoopMapper())
messageMappers.addMapper<DropdownItems, DropdownItems>(DropdownItems, createNoopMapper())
// attachments
messageMappers.addMapper<SchemaAttachments_AttachmentItem, AttachmentItem>(SchemaAttachments_AttachmentItem, {
  revertMessage (protobuf) {
    return {
      ...protobuf,
      subitems: protobuf.subitems.length ? protobuf.subitems : undefined,
    }
  },
})
